import jsPDF from 'jspdf';
import { Bill, Trip } from '@/types';

export const exportBillToPdf = async (bill: Bill, trip: Trip): Promise<void> => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  let yPosition = margin;

  // Helper function to add text with automatic line breaks
  const addText = (text: string, x: number, y: number, options: { fontSize?: number; fontWeight?: string } = {}) => {
    const fontSize = options.fontSize || 10;
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', options.fontWeight || 'normal');
    
    const lines = pdf.splitTextToSize(text, pageWidth - 2 * margin);
    pdf.text(lines, x, y);
    return y + (lines.length * fontSize * 0.352778); // Convert pt to mm
  };

  // Add header
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Rechnung', margin, yPosition);
  yPosition += 15;

  // Trip information
  yPosition = addText(`Reise: ${trip.name}`, margin, yPosition, { fontSize: 12, fontWeight: 'bold' });
  yPosition = addText(`Ziel: ${trip.destination}`, margin, yPosition);
  yPosition = addText(`Zeitraum: ${new Date(trip.startDate).toLocaleDateString('de-DE')} - ${new Date(trip.endDate).toLocaleDateString('de-DE')}`, margin, yPosition);
  yPosition += 10;

  // Bill information
  yPosition = addText(`Apartment: ${bill.apartmentName}`, margin, yPosition, { fontSize: 14, fontWeight: 'bold' });
  yPosition = addText(`Rechnungsnummer: ${bill.id}`, margin, yPosition);
  yPosition = addText(`Erstellt am: ${bill.generatedAt.toLocaleDateString('de-DE')}`, margin, yPosition);
  yPosition = addText(`Status: ${getBillStatusText(bill.status)}`, margin, yPosition);
  yPosition += 15;

  // Table header
  const tableStartY = yPosition;
  const col1 = margin;
  const col2 = margin + 60;
  const col3 = margin + 100;
  const col4 = margin + 130;
  const col5 = margin + 160;
  const rowHeight = 8;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  
  // Header row
  pdf.text('Name', col1, yPosition);
  pdf.text('Unterkunft', col2, yPosition);
  pdf.text('Skipass', col3, yPosition);
  pdf.text('Transport', col4, yPosition);
  pdf.text('Gesamt', col5, yPosition);
  yPosition += rowHeight;

  // Draw header line
  pdf.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
  yPosition += 2;

  pdf.setFont('helvetica', 'normal');

  // Table data
  bill.travellers.forEach((traveller) => {
    pdf.text(traveller.name, col1, yPosition);
    pdf.text(`€${traveller.accommodationCost.toFixed(2)}`, col2, yPosition);
    pdf.text(`€${traveller.skiPassCost.toFixed(2)}`, col3, yPosition);
    pdf.text(`€${traveller.transportationCost.toFixed(2)}`, col4, yPosition);
    pdf.text(`€${traveller.total.toFixed(2)}`, col5, yPosition);
    yPosition += rowHeight;
  });

  // Total line
  yPosition += 5;
  pdf.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
  yPosition += 2;

  pdf.setFont('helvetica', 'bold');
  pdf.text('Gesamtsumme:', col1, yPosition);
  pdf.text(`€${bill.grandTotal.toFixed(2)}`, col5, yPosition);
  yPosition += 15;

  // Payment information
  if (bill.isPaid) {
    yPosition = addText('Status: Vollständig bezahlt', margin, yPosition, { fontSize: 12, fontWeight: 'bold' });
    if (bill.paidAt) {
      yPosition = addText(`Bezahlt am: ${bill.paidAt.toLocaleDateString('de-DE')}`, margin, yPosition);
    }
  } else if (bill.paidAmount > 0) {
    yPosition = addText(`Teilzahlung erhalten: €${bill.paidAmount.toFixed(2)}`, margin, yPosition, { fontSize: 12, fontWeight: 'bold' });
    yPosition = addText(`Ausstehender Betrag: €${(bill.grandTotal - bill.paidAmount).toFixed(2)}`, margin, yPosition);
  } else {
    yPosition = addText('Status: Unbezahlt', margin, yPosition, { fontSize: 12, fontWeight: 'bold' });
  }

  // Footer
  const footerY = pageHeight - 30;
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Erstellt mit Reiseleiter Pro', margin, footerY);
  pdf.text(`Generiert am: ${new Date().toLocaleDateString('de-DE')} ${new Date().toLocaleTimeString('de-DE')}`, margin, footerY + 5);

  // Save the PDF
  const filename = `Rechnung_${bill.apartmentName}_${bill.id.slice(-6)}.pdf`;
  pdf.save(filename);
};

const getBillStatusText = (status: string): string => {
  switch (status) {
    case 'draft': return 'Entwurf';
    case 'generated': return 'Erstellt';
    case 'sent': return 'Versendet';
    case 'partially_paid': return 'Teilweise bezahlt';
    case 'fully_paid': return 'Vollständig bezahlt';
    default: return status;
  }
};